import {Fade, Popover} from "@mui/material";
import {AuthActions} from "crqlar-react-common/dist/state/auth.reducer";
import React from "react";
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {syncMixpanelAuth} from "../../../utils/analytics/mixpanelAuth";
import ChangeMyPasswordDialog from "../../identity/ChangeMyPasswordDialog";
import {SIDEBAR_CONTENT_BORDER_RADIUS} from "./sidebarLayout";
import {UserMenuButton} from "./UserMenuButton";
import {UserMenuPopoverContent} from "./UserMenuPopoverContent";

interface UserMenuProps {
    expanded: boolean;
    onOpenChange?: (open: boolean, pointerPosition?: UserMenuPointerPosition) => void;
}

export interface UserMenuPointerPosition {
    x: number;
    y: number;
}

const getPointerPosition = (event: unknown): UserMenuPointerPosition | undefined => {
    const maybeEvent = event as {
        clientX?: unknown;
        clientY?: unknown;
        nativeEvent?: {clientX?: unknown; clientY?: unknown};
    } | undefined;
    const clientX = typeof maybeEvent?.clientX === "number"
        ? maybeEvent.clientX
        : maybeEvent?.nativeEvent?.clientX;
    const clientY = typeof maybeEvent?.clientY === "number"
        ? maybeEvent.clientY
        : maybeEvent?.nativeEvent?.clientY;

    return typeof clientX === "number" && typeof clientY === "number"
        ? {x: clientX, y: clientY}
        : undefined;
};

const UserMenu = ({expanded, onOpenChange}: UserMenuProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [languageAnchorEl, setLanguageAnchorEl] = React.useState<HTMLElement | null>(null);
    const [open, setOpen] = React.useState(false);
    const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false);
    const closePointerPositionRef = React.useRef<UserMenuPointerPosition | undefined>(undefined);
    const latestPointerPositionRef = React.useRef<UserMenuPointerPosition | undefined>(undefined);

    React.useEffect(() => {
        if (!open) return undefined;

        const handlePointerMove = (event: PointerEvent) => {
            latestPointerPositionRef.current = {x: event.clientX, y: event.clientY};
        };

        window.addEventListener("pointermove", handlePointerMove, {passive: true});
        return () => window.removeEventListener("pointermove", handlePointerMove);
    }, [open]);

    const handleClose = (event?: unknown) => {
        closePointerPositionRef.current = getPointerPosition(event) ?? latestPointerPositionRef.current;
        setLanguageAnchorEl(null);
        setOpen(false);
    };

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (open) {
            handleClose();
            return;
        }

        const pointerPosition = getPointerPosition(event);
        closePointerPositionRef.current = undefined;
        latestPointerPositionRef.current = pointerPosition;
        setAnchorEl(event.currentTarget);
        setOpen(true);
        onOpenChange?.(true, pointerPosition);
    };

    const handleCloseLanguageMenu = (event?: unknown) => {
        handleClose(event);
    };

    const handlePopoverExited = () => {
        setAnchorEl(null);
        onOpenChange?.(false, closePointerPositionRef.current);
        closePointerPositionRef.current = undefined;
        latestPointerPositionRef.current = undefined;
    };

    const handleLogout = () => {
        handleClose();
        localStorage.removeItem("token");
        syncMixpanelAuth(undefined);
        dispatch({type: AuthActions.SET_USER, payload: undefined});
        navigate("/identity/login");
    };

    const handleChangePassword = () => {
        handleClose();
        setPasswordDialogOpen(true);
    };

    return (
        <>
            <UserMenuButton expanded={expanded} open={open} onClick={handleOpen}/>

            <Popover
                id="dashboard-user-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                TransitionComponent={Fade}
                transitionDuration={120}
                TransitionProps={{onExited: handlePopoverExited}}
                anchorOrigin={{vertical: "top", horizontal: "right"}}
                transformOrigin={{vertical: "bottom", horizontal: "left"}}
                PaperProps={{
                    sx: {
                        width: {xs: "calc(100vw - 24px)", sm: 336},
                        maxWidth: 336,
                        mt: 1,
                        borderRadius: SIDEBAR_CONTENT_BORDER_RADIUS,
                        overflow: "hidden",
                        boxShadow: "0 16px 40px rgba(15, 23, 42, 0.18)",
                    },
                }}
            >
                <UserMenuPopoverContent
                    languageAnchorEl={languageAnchorEl}
                    onClose={handleClose}
                    onCloseLanguageMenu={handleCloseLanguageMenu}
                    onChangePassword={handleChangePassword}
                    onLogout={handleLogout}
                    onOpenLanguageMenu={event => setLanguageAnchorEl(event.currentTarget)}
                />
            </Popover>

            {/* Let the popover finish closing and restore focus before opening the dialog. */}
            <ChangeMyPasswordDialog
                open={passwordDialogOpen && anchorEl === null}
                onClose={() => setPasswordDialogOpen(false)}
            />
        </>
    );
};

export default UserMenu;
